import React, {useState} from "react";
import {observer} from "mobx-react";
import {Alert, Button, Card, Col, Container, Form, Row} from "react-bootstrap";
import * as yup from "yup";
import * as formik from "formik";
import HeroSection from "../../components/HeroSection.jsx";
import {accountApi} from "../../api/account.js";
import {useNavigate} from "react-router-dom";

const schema = yup.object().shape({
    currentPassword: yup.string().required("Current password is required"),
    newPassword: yup.string()
        .required("New password is required")
        .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[^\s]{8,}$/, "Must be 8+ chars with uppercase, lowercase, and a number"),
    confirmPassword: yup.string()
        .required("Confirm your new password")
        .oneOf([yup.ref("newPassword")], "Passwords must match")
});

function ChangePasswordPage() {
    const {Formik} = formik;
    const navigate = useNavigate();
    const [submitError, setSubmitError] = useState("");
    const [submitSuccess, setSubmitSuccess] = useState("");

    return (
        <Container className="dashboard-shell">
            <HeroSection
                accent="customer"
                eyebrow="Security"
                title="Change Password"
                subtitle="Update your password to keep your account secure."
            />
            <Row className="justify-content-center mt-1">
                <Col lg={6}>
                    <Card className="dashboard-panel">
                        <Card.Body>
                            <Formik
                                validationSchema={schema}
                                onSubmit={async (values, {setSubmitting, resetForm}) => {
                                    setSubmitError("");
                                    setSubmitSuccess("");
                                    try {
                                        await accountApi.changePassword(values.currentPassword, values.newPassword);
                                        setSubmitSuccess("Password updated successfully.");
                                        resetForm();
                                    } catch (err) {
                                        setSubmitError("Unable to update password. Please confirm your current password and try again.");
                                    } finally {
                                        setSubmitting(false);
                                    }
                                }}
                                initialValues={{
                                    currentPassword: "",
                                    newPassword: "",
                                    confirmPassword: ""
                                }}
                            >
                                {({handleSubmit, handleChange, values, touched, errors, isSubmitting}) => (
                                    <Form noValidate onSubmit={handleSubmit}>
                                        <Form.Group className="mb-3" controlId="currentPassword">
                                            <Form.Label>Current Password</Form.Label>
                                            <Form.Control
                                                type="password"
                                                name="currentPassword"
                                                value={values.currentPassword}
                                                onChange={handleChange}
                                                isInvalid={touched.currentPassword && !!errors.currentPassword}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.currentPassword}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                        <Form.Group className="mb-3" controlId="newPassword">
                                            <Form.Label>New Password</Form.Label>
                                            <Form.Control
                                                type="password"
                                                name="newPassword"
                                                value={values.newPassword}
                                                onChange={handleChange}
                                                isInvalid={touched.newPassword && !!errors.newPassword}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.newPassword}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                        <Form.Group className="mb-3" controlId="confirmPassword">
                                            <Form.Label>Confirm New Password</Form.Label>
                                            <Form.Control
                                                type="password"
                                                name="confirmPassword"
                                                value={values.confirmPassword}
                                                onChange={handleChange}
                                                isInvalid={touched.confirmPassword && !!errors.confirmPassword}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.confirmPassword}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                        {submitError && <Alert variant="danger" className="mb-2">{submitError}</Alert>}
                                        {submitSuccess && <Alert variant="success" className="mb-2">{submitSuccess}</Alert>}
                                        <div className="d-flex justify-content-end gap-2">
                                            <Button variant="outline-secondary" type="button" onClick={() => navigate(-1)}>
                                                Cancel
                                            </Button>
                                            <Button type="submit" disabled={isSubmitting}>
                                                {isSubmitting ? "Updating..." : "Update Password"}
                                            </Button>
                                        </div>
                                    </Form>
                                )}
                            </Formik>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
}

export default observer(ChangePasswordPage);
